import React from 'react'
import { defineMessages } from 'react-intl'

import FAQItem from 'components/FaqItem'
import PageSubTitle from 'components/Page/Page.SubTitle.js'
import { View } from 'components/View.js'
import markdownToHtmlStringUtil from 'utils/string/markdown-to-html'
import useIntl from 'modules/component/utils/hooks/useIntl'
import Video from 'components/Video/index.js'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import FitnessToolsFAQItems from 'components/FitnessToolsFAQItems'

import './index.css'

const workoutMessage = defineMessages({
  title: {
    id: 'AppFAQItems.workoutTitle',
    defaultMessage: 'Workouts & Training Plans ⭐️'
  },
  personalizedPlansQuestion: {
    id: 'AppFAQItems.personalizedPlansQuestion',
    defaultMessage: 'How do personalized workout plans work?'
  },
  personalizedPlansAnswer: {
    id: 'AppFAQItems.personalizedPlansAnswer',
    defaultMessage:
      'We’ll ask a few questions about your goals, fitness level, and experience. Then we’ll suggest you a workout plan just for you.'
  },
  smartPlanQuestion: {
    id: 'AppFAQItems.smartPlanQuestion',
    defaultMessage: 'What is the smart plan & workout builder?'
  },
  smartPlanAnswer: {
    id: 'AppFAQItems.smartPlanAnswer',
    defaultMessage:
      'It’s a tool that helps you create your own plan based on your goals, schedule, and equipment.'
  },
  createWorkoutsVoiceQuestion: {
    id: 'AppFAQItems.createWorkoutsVoiceQuestion',
    defaultMessage: 'Can I create workouts using my voice?'
  },
  createWorkoutsVoiceAnswer: {
    id: 'AppFAQItems.createWorkoutsVoiceAnswer',
    defaultMessage:
      'Yes. Say what you want to train and how long you want to work out, e.g. "A 20-min core workout". The smart workout builder will generate a workout based on your equipment and fitness level.'
  },
  createWorkoutsTypingQuestion: {
    id: 'AppFAQItems.createWorkoutsTypingQuestion',
    defaultMessage: 'Can I create workouts by typing?'
  },
  createWorkoutsTypingAnswer: {
    id: 'AppFAQItems.createWorkoutsTypingAnswer',
    defaultMessage:
      'Yes. Type what you want to train and how long you want to work out, e.g. "A 30-min leg workout". The smart workout builder will generate a workout based on your equipment and fitness level.'
  },
  workoutRemindersQuestion: {
    id: 'AppFAQItems.workoutRemindersQuestion',
    defaultMessage: 'How do workout reminders work?'
  },
  workoutRemindersAnswer: {
    id: 'AppFAQItems.workoutRemindersAnswer',
    defaultMessage:
      'You can set reminders to get notifications when it’s time to work out. It works like a calendar reminder to help you stay consistent.'
  },
  recommendedWorkoutsQuestion: {
    id: 'AppFAQItems.recommendedWorkoutsQuestion',
    defaultMessage: 'What are recommended workouts?'
  },
  recommendedWorkoutsAnswer: {
    id: 'AppFAQItems.recommendedWorkoutsAnswer',
    defaultMessage:
      'These are free workouts created by our coach that you can follow anytime. They’re great when you want variety or something different from your main program.'
  },
  workoutTrackingQuestion: {
    id: 'AppFAQItems.workoutTrackingQuestion',
    defaultMessage: 'How does the workout tracker work?'
  },
  workoutTrackingAnswer: {
    id: 'AppFAQItems.workoutTrackingAnswer',
    defaultMessage: `You can log sets, reps, weight, and rest time. The tracker remembers your past results so you can build on your progress.

If an exercise is missing, you can create your own and add it to your workouts.`
  },
  customExerciseQuestion: {
    id: 'AppFAQItems.customExerciseQuestion',
    defaultMessage: 'Can I add my own exercise?'
  },
  customExerciseAnswer: {
    id: 'AppFAQItems.customExerciseAnswer',
    defaultMessage:
      'Yes. If an exercise isn’t available, you can create your own and add it to your workouts just like any other movement.'
  },
  exerciseVideosQuestion: {
    id: 'AppFAQItems.exerciseVideosQuestion',
    defaultMessage: 'Do you offer video demonstrations?'
  },
  exerciseVideosAnswer: {
    id: 'AppFAQItems.exerciseVideosAnswer',
    defaultMessage:
      'Yes. Most exercises in the app include videos demo to help you learn proper form and technique.'
  },
  offlineWorkoutsQuestion: {
    id: 'AppFAQItems.offlineWorkoutsQuestion',
    defaultMessage: 'Can I download my workouts for offline use?'
  },
  offlineWorkoutsAnswer: {
    id: 'AppFAQItems.offlineWorkoutsAnswer',
    defaultMessage:
      'Press “📥 Download” inside a workout and we will download it so you can access it without internet. It’s perfect for a gym that has spotty reception or if you’re traveling.'
  }
})

const nutritionMessage = defineMessages({
  title: {
    id: 'AppFAQItems.nutritionTitle',
    defaultMessage: 'Nutrition & Calorie Tracking 💎'
  },
  trackCaloriesPhotoQuestion: {
    id: 'AppFAQItems.trackCaloriesPhotoQuestion',
    defaultMessage: 'How do I track calories with a photo?'
  },
  trackCaloriesPhotoAnswer: {
    id: 'AppFAQItems.trackCaloriesPhotoAnswer',
    defaultMessage:
      'Take a photo of your meal and our AI will estimate the calories and nutrients for you.'
  },
  calorieScannerAccuracyQuestion: {
    id: 'AppFAQItems.calorieScannerAccuracyQuestion',
    defaultMessage: 'How accurate is the AI calorie scanner?'
  },
  calorieScannerAccuracyAnswer: {
    id: 'AppFAQItems.calorieScannerAccuracyAnswer',
    defaultMessage:
      'The scanner provides a smart estimate based on the photo you take. Accuracy is best with clear photos in good lighting and common meals. You can always edit the result.'
  },
  trackCaloriesTextQuestion: {
    id: 'AppFAQItems.trackCaloriesTextQuestion',
    defaultMessage: 'How do I track calories by typing them?'
  },
  trackCaloriesTextAnswer: {
    id: 'AppFAQItems.trackCaloriesTextAnswer',
    defaultMessage:
      'You can type in a description of your meal (e.g. “grilled chicken with rice and broccoli”), and our AI will estimate the calories and macros for you. You can always review and edit the result.'
  },
  trackCaloriesVoiceQuestion: {
    id: 'AppFAQItems.trackCaloriesVoiceQuestion',
    defaultMessage: 'Can I track calories using my voice?'
  },
  trackCaloriesVoiceAnswer: {
    id: 'AppFAQItems.trackCaloriesVoiceAnswer',
    defaultMessage:
      'Yes. Just describe your meal out loud (e.g. “a turkey sandwich with cheese”), and our AI will log the estimated calories and macros for you. You can edit the result if needed.'
  },
  editMacrosManuallyQuestion: {
    id: 'AppFAQItems.editMacrosManuallyQuestion',
    defaultMessage: 'Can I edit the macros manually?'
  },
  editMacrosManuallyAnswer: {
    id: 'AppFAQItems.editMacrosManuallyAnswer',
    defaultMessage:
      'Yes. After our AI generates the estimate, you can manually adjust the calories, protein, carbs, and fat.'
  }
})

const accountMessage = defineMessages({
  title: {
    id: 'AppFAQItems.accountTitle',
    defaultMessage: 'Account 👤'
  },
  deleteAccountQuestion: {
    id: 'FaqAccount.deleteAccountQuestion',
    defaultMessage: 'How do I delete my account?'
  },
  deleteAccountAnswer: {
    id: 'FaqAccount.deleteAccountAnswer',
    defaultMessage: `You can delete your account anytime from your [account page](/account/edit?keyToEdit=SUBSCRIPTION "account page").

Your active subscriptions will get deleted and you won't get charged in the future.


Note that your data will be deleted permanently and you will lose access to your account immediately.`
  },
  changeEmailQuestion: {
    id: 'AppFAQItems.changeEmailQuestion',
    defaultMessage: 'How do I change my email?'
  },
  changeEmailAnswer: {
    id: 'AppFAQItems.changeEmailAnswer',
    defaultMessage: `You can change your email from your [account page](/account/edit?keyToEdit=SUBSCRIPTION "account page").

After changing your email address, you will receive a confirmation to your old email address.

You will also need to verify your new email address by pressing the "verify" button as shown in the video.`
  }
})

const subscriptionMessage = defineMessages({
  title: {
    id: 'AppFAQItems.subscriptionTitle',
    defaultMessage: 'Subscription & Billing  💳'
  },
  tryPremiumQuestion: {
    id: 'AppFAQItems.tryPremiumQuestion',
    defaultMessage: 'How to try premium for free?'
  },
  tryPremiumAnswer: {
    id: 'AppFAQItems.tryPremiumAnswer',
    defaultMessage: `You can [try premium free](/premium "try premium free") for 7 days. You can [unsubscribe](/account/edit?keyToEdit=SUBSCRIPTION "unsubscribe premium") anytime.

More info in our [terms and conditions.](/terms "terms and conditions")`
  },
  trialChargeQuestion: {
    id: 'FaqAccount.trialChargeQuestion',
    defaultMessage: 'Will I get charged if I start a free trial?'
  },
  trialChargeAnswer: {
    id: 'FaqAccount.trialChargeAnswer',
    defaultMessage: `The trial is free for 7 days. You can [unsubscribe](/account/edit?keyToEdit=SUBSCRIPTION "unsubscribe premium") anytime. You will not get charged if you cancel before the trial ends.

If you haven’t cancelled by then, you will automatically get subscribed to the [premium plan you choose](/premium "premium plan you choose").`
  },
  switchToYearlyQuestion: {
    id: 'AppFAQItems.switchToYearlyQuestion',
    defaultMessage: 'Can I switch from monthly to yearly?'
  },
  switchToYearlyAnswer: {
    id: 'AppFAQItems.switchToYearlyAnswer',
    defaultMessage: `Yes, you can switch from a monthly to a yearly plan anytime from your [account page](/account/edit?keyToEdit=SUBSCRIPTION "account page").

When you switch, we’ll charge you the yearly price minus the remaining value of your current monthly plan, so you’re only paying the difference.`
  },
  midCycleUpgradeQuestion: {
    id: 'AppFAQItems.midCycleUpgradeQuestion',
    defaultMessage: 'What happens if I upgrade mid-cycle?'
  },
  midCycleUpgradeAnswer: {
    id: 'AppFAQItems.midCycleUpgradeAnswer',
    defaultMessage: `You won’t be charged twice.

We’ll only charge you the difference between your current plan and the one you’re upgrading to, based on the remaining time in your current billing cycle.`
  },
  cancelSubscriptionQuestion: {
    id: 'AppFAQItems.cancelSubscriptionQuestion',
    defaultMessage: 'How to cancel my subscription?'
  },
  cancelSubscriptionAnswer: {
    id: 'AppFAQItems.cancelSubscriptionAnswer',
    defaultMessage: `You can [unsubscribe](/account/edit?keyToEdit=SUBSCRIPTION "unsubscribe premium") anytime. Cancelling your subscription will make you lose access to premium features immediately.

Here’s a tutorial on how to cancel your subscription:`
  },
  cancelTrialQuestion: {
    id: 'AppFAQItems.cancelTrialQuestion',
    defaultMessage: 'How to cancel my free trial?'
  },
  cancelTrialAnswer: {
    id: 'AppFAQItems.cancelTrialAnswer',
    defaultMessage: `You can [cancel your free trial](/account/edit?keyToEdit=SUBSCRIPTION "unsubscribe premium") anytime. You will not get charged if you cancel before the trial ends. Cancelling your free trial will make you lose access to premium features immediately.

Here’s a tutorial on how to cancel your subscription:`
  },
  cannotFindSubscriptionQuestion: {
    id: 'AppFAQItems.cannotFindSubscriptionQuestion',
    defaultMessage: 'Trying to cancel, but can’t find your subscription?'
  },
  cannotFindSubscriptionAnswer: {
    id: 'AppFAQItems.cannotFindSubscriptionAnswer',
    defaultMessage:
      'Contact us at contact@gymaholic.co and provide the last 4 digits of your card so we can look up your subscription and cancel it for you'
  },
  planDifferenceQuestion: {
    id: 'AppFAQItems.planDifferenceQuestion',
    defaultMessage:
      'What’s the difference between Premium ⭐ and Premium Plus 💎?'
  },
  planDifferenceAnswer: {
    id: 'AppFAQItems.planDifferenceAnswer',
    defaultMessage: `Premium ⭐ primarily focuses on personalized workout plans and progress monitoring. It’s great for those who want to build and follow workout routines.

Premium Plus 💎 includes everything in Premium ⭐.

Premium Plus 💎 also allows you to track calories with a photo and monitoring your macros, perfect for those who want to combine workout and nutrition.`
  },
  downgradeImpactQuestion: {
    id: 'AppFAQItems.downgradeImpactQuestion',
    defaultMessage:
      'What happens if I downgrade from Premium Plus 💎 to Premium ⭐?'
  },
  downgradeImpactAnswer: {
    id: 'AppFAQItems.downgradeImpactAnswer',
    defaultMessage:
      'You will keep access to your meals history if you downgrade from Premium Plus 💎 to Premium ⭐.'
  },
  refundPolicyQuestion: {
    id: 'AppFAQItems.refundPolicyQuestion',
    defaultMessage: 'Do you offer refunds?'
  },
  refundPolicyAnswer: {
    id: 'AppFAQItems.refundPolicyAnswer',
    defaultMessage:
      'Refunds only apply if you contacted our support team **before your new billing cycle started** and were unable to [cancel your subscription](/account/edit?keyToEdit=SUBSCRIPTION "cancel your subscription") in time.'
  }
})

const FAQ_ITEMS = [
  // 1) Workouts & Training Plans
  {
    title: workoutMessage.title,
    items: [
      {
        question: workoutMessage.personalizedPlansQuestion,
        answer: workoutMessage.personalizedPlansAnswer
      },
      {
        question: workoutMessage.smartPlanQuestion,
        answer: workoutMessage.smartPlanAnswer
      },
      {
        question: workoutMessage.createWorkoutsVoiceQuestion,
        answer: workoutMessage.createWorkoutsVoiceAnswer
      },
      {
        question: workoutMessage.createWorkoutsTypingQuestion,
        answer: workoutMessage.createWorkoutsTypingAnswer
      },
      {
        question: workoutMessage.workoutRemindersQuestion,
        answer: workoutMessage.workoutRemindersAnswer
      },
      {
        question: workoutMessage.recommendedWorkoutsQuestion,
        answer: workoutMessage.recommendedWorkoutsAnswer
      },
      {
        question: workoutMessage.workoutTrackingQuestion,
        answer: workoutMessage.workoutTrackingAnswer
      },
      {
        question: workoutMessage.exerciseVideosQuestion,
        answer: workoutMessage.exerciseVideosAnswer
      },
      {
        question: workoutMessage.customExerciseQuestion,
        answer: workoutMessage.customExerciseAnswer
      },
      {
        question: workoutMessage.offlineWorkoutsQuestion,
        answer: workoutMessage.offlineWorkoutsAnswer
      }
    ]
  },

  // 2) Nutrition & Calorie Tracking
  {
    title: nutritionMessage.title,
    items: [
      {
        question: nutritionMessage.trackCaloriesPhotoQuestion,
        answer: nutritionMessage.trackCaloriesPhotoAnswer
      },
      {
        question: nutritionMessage.calorieScannerAccuracyQuestion,
        answer: nutritionMessage.calorieScannerAccuracyAnswer
      },
      {
        question: nutritionMessage.trackCaloriesTextQuestion,
        answer: nutritionMessage.trackCaloriesTextAnswer
      },
      {
        question: nutritionMessage.trackCaloriesVoiceQuestion,
        answer: nutritionMessage.trackCaloriesVoiceAnswer
      },
      {
        question: nutritionMessage.editMacrosManuallyQuestion,
        answer: nutritionMessage.editMacrosManuallyAnswer
      }
    ]
  },

  // 3) Subscription & Billing
  {
    title: subscriptionMessage.title,
    items: [
      {
        question: subscriptionMessage.planDifferenceQuestion,
        answer: subscriptionMessage.planDifferenceAnswer
      },
      {
        question: subscriptionMessage.tryPremiumQuestion,
        answer: subscriptionMessage.tryPremiumAnswer
      },
      {
        question: subscriptionMessage.trialChargeQuestion,
        answer: subscriptionMessage.trialChargeAnswer
      },
      {
        question: subscriptionMessage.cancelTrialQuestion,
        answer: subscriptionMessage.cancelTrialAnswer,
        video:
          'https://cdn.gymaholic.co/pages/faq/videos/cancel-subscription.mp4',
        poster:
          'https://cdn.gymaholic.co/pages/faq/images/cancel-subscription.png'
      },
      {
        question: subscriptionMessage.cancelSubscriptionQuestion,
        answer: subscriptionMessage.cancelSubscriptionAnswer,
        video:
          'https://cdn.gymaholic.co/pages/faq/videos/cancel-subscription.mp4',
        poster:
          'https://cdn.gymaholic.co/pages/faq/images/cancel-subscription.png'
      },
      {
        question: subscriptionMessage.cannotFindSubscriptionQuestion,
        answer: subscriptionMessage.cannotFindSubscriptionAnswer
      },
      {
        question: subscriptionMessage.refundPolicyQuestion,
        answer: subscriptionMessage.refundPolicyAnswer
      },
      {
        question: subscriptionMessage.switchToYearlyQuestion,
        answer: subscriptionMessage.switchToYearlyAnswer
      },
      {
        question: subscriptionMessage.midCycleUpgradeQuestion,
        answer: subscriptionMessage.midCycleUpgradeAnswer
      },
      {
        question: subscriptionMessage.downgradeImpactQuestion,
        answer: subscriptionMessage.downgradeImpactAnswer
      }
    ]
  },

  // 4) Account
  {
    title: accountMessage.title,
    items: [
      {
        question: accountMessage.changeEmailQuestion,
        answer: accountMessage.changeEmailAnswer,
        video:
          'https://cdn.gymaholic.co/pages/faq/videos/change-email.mp4',
        poster:
          'https://cdn.gymaholic.co/pages/faq/images/change-email.png'
      },
      {
        question: accountMessage.deleteAccountQuestion,
        answer: accountMessage.deleteAccountAnswer,
        video:
          'https://cdn.gymaholic.co/pages/faq/videos/delete-account.mp4',
        poster:
          'https://cdn.gymaholic.co/pages/faq/images/delete-account.png'
      }
    ]
  }
]

export function AppFAQItems () {
  const intl = useIntl()
  return (
    <View className='app-faq-items'>
      {FAQ_ITEMS.map((section) => {
        const {
          title,
          items
        } = section
        const titleStr = formatMessageIntlUtil(intl, title)
        return (
          <View key={titleStr}>
            <PageSubTitle>{titleStr}</PageSubTitle>
            {items.map((item) => {
              const {
                question,
                answer,
                video,
                poster
              } = item
              const questionStr = formatMessageIntlUtil(intl, question)
              const answerStr = formatMessageIntlUtil(intl, answer)
              return (
                <FAQItem
                  key={questionStr}
                  question={questionStr}
                  answer={
                    <View>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: markdownToHtmlStringUtil(answerStr)
                        }}
                      />
                      {video && (
                        <View mt>
                          <Video
                            source={video}
                            poster={poster}
                          />
                        </View>
                      )}
                    </View>
                  }
                />
              )
            })}
          </View>
        )
      })}

      {/* Fitness Tools FAQ Section */}
      <FitnessToolsFAQItems />
    </View>
  )
}
